import React, { useRef } from 'react'
import { OfferPageMultiRoomAutoselectionReason, OfferPageMultiRoomMode, OfferPageRoom } from 'contexts/OfferPage/offerPageStateReducer'
import { withOfferPageAndReduxState } from 'contexts/OfferPage/withOfferPageState'
import VerticalSpacer from 'components/Common/Spacing/VerticalSpacer'
import Sheet from 'components/utils/Sheet'
import OfferPageRoomModeToggleSection from 'components/OfferPage/MultipleRoomsBooking/Mobile/OfferPageRoomModeToggleSection'
import OfferPageTogglesSection from 'components/OfferPage/MultipleRoomsBooking/Mobile/OfferPageTogglesSection'
import { getCanUseMultiRoomUI } from 'selectors/offerPage/offerPageSelectors'
import { useOfferPageFlightPrice } from 'hooks/useFlightPrice'
import ReturnCharterFlightTile from 'components/Flights/CharterBundle/ReturnCharterFlightTile'
import { FlightsClassTypes } from 'constants/flight'
import OfferPagePackagesHeading from 'components/OfferPage/MultipleRoomsBooking/Mobile/OfferPagePackagesHeading'
import CharterDurationToggle from './CharterDurationToggle'
import Pane from 'components/Common/Pane'
import styled from 'styled-components'
import { rem } from 'polished'
import FlashHotelMobileIndividualRooms from 'components/OfferPage/FlashHotel/FlashHotelMobileVariant/FlashHotelMobileIndividualRooms'
import FlashHotelMobileSameRoomOptions from './SameRoom/FlashHotelMobileSameRoomOptions'
import { isMultipleRoomBookingActive } from 'selectors/offerPage/offerPageSharedSelectors'
import LayoutContainer from 'components/Common/LayoutContainer'
import Caption from 'LuxKit/Typography/Caption'
import config from 'constants/config'
import { useIntl } from 'react-intl'

const CharterDurationPane = styled(Pane)`
  padding: ${rem(20)};
  background-color: ${({ theme }) => theme.palette.neutral.default.one};
`
interface Props {
  checkInDate?: string
  checkOutDate?: string
  offer: App.HotelOffer
  onViewPackageOptions?: () => void
  rooms: Array<OfferPageRoom>
  canUseMultiRoomUI: boolean
  roomMode: OfferPageMultiRoomMode
  autoselectionReason?: OfferPageMultiRoomAutoselectionReason
  flightOrigin?: App.Airport
  showFlightInformation: boolean
  charterBundleRoute?: App.CharterBundleRoute
  fareClass: FlightsClassTypes
  multipleRoomBookingActive: boolean
}

function FlashHotelMobileRoomOptions(props: Props) {
  const {
    checkInDate,
    checkOutDate,
    offer,
    rooms,
    canUseMultiRoomUI,
    roomMode,
    flightOrigin,
    showFlightInformation,
    autoselectionReason,
    charterBundleRoute,
    fareClass,
    multipleRoomBookingActive,
  } = props

  const intl = useIntl()
  const hasCharterBundle = !!charterBundleRoute

  const { totalFlightPrice } = useOfferPageFlightPrice()

  const headerRef = useRef<HTMLDivElement | null>(null)

  return <Sheet
    background="neutral-six"
    padding={hasCharterBundle ? '0 0 8 0' : '8 0'}
  >
    <VerticalSpacer gap={8}>
      {hasCharterBundle && <>
        <CharterDurationPane>
          <CharterDurationToggle />
        </CharterDurationPane>
        {charterBundleRoute && <ReturnCharterFlightTile
          route={charterBundleRoute}
          occupancies={rooms}
          checkInDate={checkInDate}
          checkOutDate={checkOutDate}
          fareClass={fareClass}
          flightOrigin={flightOrigin}
        />}
      </>}

      {canUseMultiRoomUI && <OfferPageRoomModeToggleSection
        roomMode={roomMode}
        rooms={rooms}
        saleUnit={offer.saleUnit}
        autoselectionReason={autoselectionReason}
        charterBundleRoute={charterBundleRoute}
      />}
      {!canUseMultiRoomUI && <OfferPagePackagesHeading
        charterBundleRoute={charterBundleRoute}
      />}

      <OfferPageTogglesSection
        totalFlightPrice={totalFlightPrice}
        bundledWithFlightsOnly={offer.bundledWithFlightsOnly || !!charterBundleRoute}
      />

      {multipleRoomBookingActive && <FlashHotelMobileIndividualRooms />}
      {!multipleRoomBookingActive && <FlashHotelMobileSameRoomOptions showSelection={false} headerRef={headerRef} />}

      {showFlightInformation && (
        <LayoutContainer>
          <Caption variant="medium" align="center">
            {intl.formatMessage(
              {
                id: 'ZvGtz7o2JY',
                defaultMessage: '*Price is indicative and based on sample return fares (inclusive of booking fees on Low Cost Carriers). To view live pricing, conduct a full search. {brandName} full Terms and Conditions apply.',
                description: 'Disclaimer for flight information',
              },
              {
                brandName: config.brandConfig.name,
              },
            )}
          </Caption>
        </LayoutContainer>
      )}
    </VerticalSpacer>
  </Sheet>
}

type MappedProps = Pick<Props,
  | 'checkInDate'
  | 'checkOutDate'
  | 'rooms'
  | 'canUseMultiRoomUI'
  | 'roomMode'
  | 'autoselectionReason'
  | 'flightOrigin'
  | 'charterBundleRoute'
  | 'multipleRoomBookingActive'
  | 'fareClass'
> & { open: boolean }

export default withOfferPageAndReduxState((state, appState): MappedProps => ({
  checkInDate: state.checkInDate,
  checkOutDate: state.checkOutDate,
  open: !!state.activeFilter,
  rooms: state.rooms,
  canUseMultiRoomUI: getCanUseMultiRoomUI(state, appState),
  roomMode: state.multiRoomMode,
  autoselectionReason: state.multiRoomAutoselectionReason,
  flightOrigin: state.airport,
  charterBundleRoute: state.charterBundleRoute,
  multipleRoomBookingActive: isMultipleRoomBookingActive(state, appState),
  fareClass: state.fareClass,
}), FlashHotelMobileRoomOptions)
